"use client";

import React, { useRef, useEffect, useState } from "react";

interface IntersectionProps {
  threshold?: number | number[];
  root?: Element | null;
  rootMargin?: string;
  children: (props: {
    inView: boolean;
    entry: IntersectionObserverEntry | null;
    ref: (node: Element | null) => void;
  }) => React.ReactNode;
}

export const Intersection: React.FC<IntersectionProps> = ({
  threshold = 0.5,
  root = null,
  rootMargin = "0px",
  children,
}) => {
  const [inView, setInView] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const nodeRef = useRef<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!nodeRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new window.IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        setEntry(entry);
      },
      { threshold, root, rootMargin }
    );
    observerRef.current.observe(nodeRef.current);
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, root, rootMargin, nodeRef.current]);

  const setRef = (node: Element | null) => {
    nodeRef.current = node;
    if (observerRef.current) observerRef.current.disconnect();
    if (node) {
      observerRef.current = new window.IntersectionObserver(
        ([entry]) => {
          setInView(entry.isIntersecting);
          setEntry(entry);
        },
        { threshold, root, rootMargin }
      );
      observerRef.current.observe(node);
    }
  };

  return <>{children({ inView, entry, ref: setRef })}</>;
};
